/**
 * @fileoverview Shared Customer Portal configuration editor — mounted by
 *               system-web (defaults + secrets-adjacent fields) and
 *               tenant-web (tenant overrides).
 * @module ui-admin/components/PortalConfigEditor
 *
 * Responsibilities:
 * - Presentation-only editor for customerPortalConfigs fields: nav items,
 *   portal variant, branding, workflows (with template binding), and
 *   requireApprovalForChanges.
 * - Emits the full edited config via onSave — persistence is the host's job.
 *
 * Dependencies: none beyond React — host-injected config + template catalog.
 * Architecture: presentation-only per ui-admin convention; hosts wire their
 *   own local-first services (tenant-web: portalAdminService, system-web:
 *   system config service).
 * Notes: paymentGateway fields are deliberately absent — secret references
 *   are managed by system admins only, and tenant-api rejects them outright.
 */

import { useEffect, useMemo, useState } from 'react';

/**
 * @contract PortalWorkflowConfig
 * Purpose:  One customer-facing workflow definition.
 * Fields:   id, label, description, templateId, category, icon, enabled,
 *           requiresEntityLink.
 * Rules:    templateId must reference a published template (catalog injected
 *           by the host); enabled=false keeps the draft hidden from customers.
 * Sync:     persisted by the host via its portal config service.
 */
export interface PortalWorkflowConfig {
  id: string;
  label: string;
  description: string;
  templateId: string;
  templateType?: string;
  category: 'permit' | 'inspection' | 'selfCert' | 'safetySurvey' | 'question' | 'custom';
  icon: string;
  enabled: boolean;
  requiresEntityLink: boolean;
}

export interface PortalNavItem {
  id: string;
  label: string;
  icon: string;
  type: 'page' | 'workflow' | 'survey' | 'custom';
  workflowId?: string;
  target: string;
  enabled: boolean;
  order: number;
}

export interface PortalConfigValue {
  enabled?: boolean;
  publicLandingEnabled?: boolean;
  selfCertReviewWorkflow?: 'autoAccept' | 'requireInspectorReview';
  aiAssistantEnabled?: boolean;
  requireApprovalForChanges?: boolean;
  portalVariant?: 'commercial' | 'residential';
  navItems?: PortalNavItem[];
  workflows?: PortalWorkflowConfig[];
  branding?: { displayName?: string; primaryColor?: string; welcomeMessage?: string };
  contactInfo?: Record<string, unknown>;
  activityLogRetentionDays?: number;
}

export interface PortalTemplateOption {
  _id: string;
  name: string;
  version: number;
  status?: string;
}

/**
 * @component PortalConfigEditor
 * Purpose:     Shared editor for the customerPortalConfigs document.
 * Props:       value — current config (null = defaults); templates —
 *              published-template catalog for workflow binding; onSave —
 *              persistence callback returning the saved config; readonly —
 *              disables every control (system defaults preview).
 * Behavior:    Local form state initialized from `value`; Save emits the
 *              merged config. Adding a workflow creates an enabled:false
 *              draft entry so it can't leak to customers unconfigured.
 * Data Source: remote — host-owned; this component never fetches or writes.
 * Constraints: no API calls, no Dexie, no secrets fields.
 * Performance: small form — renders the full workflow list inline.
 */
export interface PortalConfigEditorProps {
  value: PortalConfigValue | null;
  templates: PortalTemplateOption[];
  onSave: (next: PortalConfigValue) => Promise<void>;
  readonly?: boolean;
}

const CATEGORY_OPTIONS: Array<{ value: PortalWorkflowConfig['category']; label: string }> = [
  { value: 'permit', label: 'Permit' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'selfCert', label: 'Self-certification' },
  { value: 'safetySurvey', label: 'Safety survey' },
  { value: 'question', label: 'Question' },
  { value: 'custom', label: 'Custom' },
];

const inputCls = 'w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm';
const labelCls = 'block text-xs font-medium text-slate-600';

/**
 * Truncate option labels — Chrome sizes a native <select> listbox to its
 * WIDEST option, so a long template name blows the open popup past the
 * viewport edge (QA #4: dropdown "not showing properly" — the oversized
 * popup rendered as a giant blank box covering the form).
 */
function optionLabel(name: string, maxLen = 60): string {
  return name.length > maxLen ? `${name.slice(0, maxLen - 1)}…` : name;
}

export function PortalConfigEditor({ value, templates, onSave, readonly }: PortalConfigEditorProps) {
  const [draft, setDraft] = useState<PortalConfigValue>({ ...(value ?? {}) });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the draft when the host delivers a newer config (e.g. the
  // api_usage_cache refresh landing after mount) — otherwise the editor keeps
  // editing a stale snapshot and the next save silently regresses the server
  // config (bound templateIds render as blank dropdowns).
  useEffect(() => {
    setDraft({ ...(value ?? {}) });
    setSaved(false);
  }, [value]);

  const workflows = useMemo(() => draft.workflows ?? [], [draft.workflows]);
  const navItems = useMemo(() => draft.navItems ?? [], [draft.navItems]);

  const update = (patch: Partial<PortalConfigValue>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  const updateWorkflow = (idx: number, patch: Partial<PortalWorkflowConfig>) => {
    const next = workflows.map((w, i) => (i === idx ? { ...w, ...patch } : w));
    update({ workflows: next });
  };

  const addWorkflow = () => {
    const next: PortalWorkflowConfig = {
      id: `wf-${Date.now().toString(36)}`,
      label: '',
      description: '',
      templateId: templates[0]?._id ?? '',
      category: 'custom',
      icon: 'clipboard-list',
      enabled: false,
      requiresEntityLink: true,
    };
    update({ workflows: [...workflows, next] });
  };

  const removeWorkflow = (idx: number) => {
    update({ workflows: workflows.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Don't materialize unset navItems as an explicit [] — the portal
      // treats a persisted [] as "publish no links". When the loaded config
      // had no navItems and the admin never added one, omit the key so the
      // portal keeps its built-in navigation.
      const payload = { ...draft };
      if (value?.navItems === undefined && payload.navItems?.length === 0) {
        delete payload.navItems;
      }
      await onSave(payload);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* General toggles */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900">General</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" disabled={readonly}
              checked={draft.enabled ?? false}
              onChange={(e) => update({ enabled: e.target.checked })} />
            Portal enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" disabled={readonly}
              checked={draft.publicLandingEnabled ?? false}
              onChange={(e) => update({ publicLandingEnabled: e.target.checked })} />
            Public landing page
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" disabled={readonly}
              checked={draft.requireApprovalForChanges ?? false}
              onChange={(e) => update({ requireApprovalForChanges: e.target.checked })} />
            Require approval for contact changes
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" disabled={readonly}
              checked={draft.aiAssistantEnabled ?? false}
              onChange={(e) => update({ aiAssistantEnabled: e.target.checked })} />
            AI assistant
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <span className={labelCls}>Portal variant</span>
            <select disabled={readonly} value={draft.portalVariant ?? 'commercial'}
              onChange={(e) => update({ portalVariant: e.target.value as 'commercial' | 'residential' })}
              className={inputCls}>
              <option value="commercial">Commercial</option>
              <option value="residential">Residential</option>
            </select>
          </div>
          <div>
            <span className={labelCls}>Self-certification review</span>
            <select disabled={readonly} value={draft.selfCertReviewWorkflow ?? 'requireInspectorReview'}
              onChange={(e) => update({ selfCertReviewWorkflow: e.target.value as 'autoAccept' | 'requireInspectorReview' })}
              className={inputCls}>
              <option value="requireInspectorReview">Inspector review required</option>
              <option value="autoAccept">Auto-accept</option>
            </select>
          </div>
        </div>
      </section>

      {/* Branding */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900">Branding</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <span className={labelCls}>Display name</span>
            <input disabled={readonly} className={inputCls}
              value={draft.branding?.displayName ?? ''}
              onChange={(e) => update({ branding: { ...draft.branding, displayName: e.target.value } })} />
          </div>
          <div>
            <span className={labelCls}>Primary color</span>
            <input disabled={readonly} type="color" className="h-9 w-full rounded border border-slate-300"
              value={draft.branding?.primaryColor || '#0f766e'}
              onChange={(e) => update({ branding: { ...draft.branding, primaryColor: e.target.value } })} />
          </div>
        </div>
        <div className="mt-3">
          <span className={labelCls}>Welcome message</span>
          <textarea disabled={readonly} rows={2} className={inputCls}
            value={draft.branding?.welcomeMessage ?? ''}
            onChange={(e) => update({ branding: { ...draft.branding, welcomeMessage: e.target.value } })} />
        </div>
      </section>

      {/* Workflows */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Workflows</h3>
          {!readonly && (
            <button type="button" onClick={addWorkflow}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              + Add workflow
            </button>
          )}
        </div>
        {workflows.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">No workflows configured — customers see an empty request picker.</p>
        )}
        <ul className="mt-3 space-y-3">
          {workflows.map((wf, idx) => (
            <li key={wf.id} className="rounded border border-slate-200 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <span className={labelCls}>Label</span>
                  <input disabled={readonly} className={inputCls} value={wf.label}
                    onChange={(e) => updateWorkflow(idx, { label: e.target.value })} />
                </div>
                <div>
                  <span className={labelCls}>Category</span>
                  <select disabled={readonly} className={inputCls} value={wf.category}
                    onChange={(e) => updateWorkflow(idx, { category: e.target.value as PortalWorkflowConfig['category'] })}>
                    {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <span className={labelCls}>Template</span>
                  <select disabled={readonly} className={inputCls} value={wf.templateId}
                    onChange={(e) => updateWorkflow(idx, { templateId: e.target.value })}>
                    <option value="">— Select template —</option>
                    {wf.templateId && !templates.some((t) => String(t._id) === String(wf.templateId)) && (
                      <option value={wf.templateId}>Template no longer available</option>
                    )}
                    {templates.map((t) => (
                      <option key={t._id} value={t._id} title={`${t.name} (v${t.version})`}>
                        {optionLabel(t.name)} (v{t.version})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" disabled={readonly} checked={wf.enabled}
                      onChange={(e) => updateWorkflow(idx, { enabled: e.target.checked })} />
                    Enabled
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" disabled={readonly} checked={wf.requiresEntityLink}
                      onChange={(e) => updateWorkflow(idx, { requiresEntityLink: e.target.checked })} />
                    Requires property
                  </label>
                </div>
              </div>
              <div className="mt-3">
                <span className={labelCls}>Description</span>
                <input disabled={readonly} className={inputCls} value={wf.description}
                  onChange={(e) => updateWorkflow(idx, { description: e.target.value })} />
              </div>
              {!readonly && (
                <button type="button" onClick={() => removeWorkflow(idx)}
                  className="mt-2 text-xs text-red-600 hover:underline">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Nav items (simple ordered list) */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Navigation items</h3>
            {navItems.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">
                No custom links — the portal shows its built-in navigation.
              </p>
            )}
          </div>
          {!readonly && (
            <button type="button"
              onClick={() => update({
                navItems: [...navItems, {
                  id: `nav-${Date.now().toString(36)}`, label: '', icon: '',
                  type: 'page', target: '/', enabled: true, order: navItems.length,
                }],
              })}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              + Add item
            </button>
          )}
        </div>
        <ul className="mt-3 space-y-2">
          {navItems.map((item, idx) => (
            <li key={item.id} className="flex items-center gap-2">
              <input disabled={readonly} className={`${inputCls} w-40`} placeholder="Label" value={item.label}
                onChange={(e) => update({
                  navItems: navItems.map((n, i) => (i === idx ? { ...n, label: e.target.value } : n)),
                })} />
              <input disabled={readonly} className={`${inputCls} flex-1`} placeholder="Route target" value={item.target}
                onChange={(e) => update({
                  navItems: navItems.map((n, i) => (i === idx ? { ...n, target: e.target.value } : n)),
                })} />
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" disabled={readonly} checked={item.enabled}
                  onChange={(e) => update({
                    navItems: navItems.map((n, i) => (i === idx ? { ...n, enabled: e.target.checked } : n)),
                  })} />
                Enabled
              </label>
              {!readonly && (
                <button type="button"
                  onClick={() => update({ navItems: navItems.filter((_, i) => i !== idx) })}
                  className="text-xs text-red-600 hover:underline">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Save */}
      {!readonly && (
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => void save()} disabled={saving}
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save configuration'}
          </button>
          {saved && <span className="text-sm text-emerald-600">Saved.</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      )}
    </div>
  );
}

export default PortalConfigEditor;
